import { roundToCents } from "@common/helpers/monetary";
import {
    computeNativeInvoiceLineAmount,
    computeNativeInvoiceLineAdjustmentAmount,
    computeNativeInvoiceTotals,
} from "./computeNativeInvoiceTotals";
import {
    NativeInvoiceAdjustmentMode,
    NativeInvoiceFormState,
    NativeInvoiceLineRow,
    NativeInvoiceTotals,
} from "./nativeInvoiceFormTypes";

export type NativeInvoicePerLineKind = "tax" | "discount" | "deposit";

export interface NativeInvoiceItemSettingsSnapshot {
    isTaxPerLineItem: boolean;
    isDiscountPerLineItem: boolean;
    isDepositPerLineItem: boolean;
    isTaxDisplayedPerLineItem: boolean;
    isDiscountDisplayedPerLineItem: boolean;
    isDepositDisplayedPerLineItem: boolean;
    tax: number;
    discount: number;
    deposit: number;
    taxMode: NativeInvoiceAdjustmentMode;
    discountMode: NativeInvoiceAdjustmentMode;
    depositMode: NativeInvoiceAdjustmentMode;
    lines: NativeInvoiceLineRow[];
}

export function takeNativeInvoiceItemSettingsSnapshot(
    state: NativeInvoiceFormState,
): NativeInvoiceItemSettingsSnapshot {
    return {
        isTaxPerLineItem: state.isTaxPerLineItem,
        isDiscountPerLineItem: state.isDiscountPerLineItem,
        isDepositPerLineItem: state.isDepositPerLineItem,
        isTaxDisplayedPerLineItem: state.isTaxDisplayedPerLineItem,
        isDiscountDisplayedPerLineItem: state.isDiscountDisplayedPerLineItem,
        isDepositDisplayedPerLineItem: state.isDepositDisplayedPerLineItem,
        tax: state.tax,
        discount: state.discount,
        deposit: state.deposit,
        taxMode: state.taxMode,
        discountMode: state.discountMode,
        depositMode: state.depositMode,
        lines: structuredClone(state.lines),
    };
}

export const PER_LINE_CALC_FLAG = {
    tax: "isTaxPerLineItem",
    discount: "isDiscountPerLineItem",
    deposit: "isDepositPerLineItem",
} as const satisfies Record<
    NativeInvoicePerLineKind,
    keyof NativeInvoiceFormState
>;

export const PER_LINE_DISPLAY_FLAG = {
    tax: "isTaxDisplayedPerLineItem",
    discount: "isDiscountDisplayedPerLineItem",
    deposit: "isDepositDisplayedPerLineItem",
} as const satisfies Record<
    NativeInvoicePerLineKind,
    keyof NativeInvoiceFormState
>;

const PER_LINE_MODE = {
    tax: "taxMode",
    discount: "discountMode",
    deposit: "depositMode",
} as const satisfies Record<
    NativeInvoicePerLineKind,
    keyof NativeInvoiceFormState
>;

function computedAdjustmentAmountForKind({
    kind,
    totals,
}: {
    kind: NativeInvoicePerLineKind;
    totals: NativeInvoiceTotals;
}): number {
    if (kind === "tax") {
        return totals.taxAmount;
    }
    if (kind === "discount") {
        return totals.discountAmount;
    }
    return totals.depositAmount;
}

export function labeledLineAdjustmentFromStored({
    storedAmount,
    mode,
    lineAmount,
}: {
    storedAmount: number;
    mode: NativeInvoiceAdjustmentMode | null | undefined;
    lineAmount: number;
}): number {
    if (mode === "percent") {
        if (lineAmount === 0) {
            return 0;
        }
        return roundToCents((storedAmount / lineAmount) * 100);
    }
    return storedAmount;
}

function allocateAmountAcrossLines({
    lines,
    amount,
}: {
    lines: NativeInvoiceLineRow[];
    amount: number;
}): number[] {
    if (lines.length === 0) {
        return [];
    }
    const lineAmounts = lines.map((line) =>
        computeNativeInvoiceLineAmount(line),
    );
    const total = lineAmounts.reduce((sum, lineAmount) => sum + lineAmount, 0);
    if (total === 0) {
        return lines.map((_, index) => (index === 0 ? amount : 0));
    }
    const amountCents = Math.round(amount * 100);
    const totalCents = Math.round(total * 100);
    let allocatedCents = 0;
    const cents = lineAmounts.map((lineAmount) => {
        const lineCents = Math.round(lineAmount * 100);
        const floored = Math.floor((amountCents * lineCents) / totalCents);
        allocatedCents += floored;
        return floored;
    });
    const remainderCents = amountCents - allocatedCents;
    if (remainderCents !== 0) {
        let largestIndex = 0;
        for (let i = 1; i < lineAmounts.length; i++) {
            if (lineAmounts[i] >= lineAmounts[largestIndex]) {
                largestIndex = i;
            }
        }
        cents[largestIndex] += remainderCents;
    }
    return cents.map((value) => value / 100);
}

export function enableNativeInvoicePerLineAdjustment({
    state,
    kind,
}: {
    state: NativeInvoiceFormState;
    kind: NativeInvoicePerLineKind;
}): NativeInvoiceFormState {
    const flag = PER_LINE_CALC_FLAG[kind];
    if (state[flag]) {
        return state;
    }

    const mode = state[PER_LINE_MODE[kind]];
    const headerValue = state[kind];
    const lineValues =
        mode === "percent"
            ? state.lines.map(() => headerValue)
            : allocateAmountAcrossLines({
                  lines: state.lines,
                  amount: headerValue,
              });
    return {
        ...state,
        [flag]: true,
        lines: state.lines.map((line, index) => ({
            ...line,
            [kind]: lineValues[index] ?? 0,
        })),
    };
}

export function disableNativeInvoicePerLineAdjustment({
    state,
    kind,
}: {
    state: NativeInvoiceFormState;
    kind: NativeInvoicePerLineKind;
}): NativeInvoiceFormState {
    const flag = PER_LINE_CALC_FLAG[kind];
    if (!state[flag]) {
        return state;
    }
    const displayFlag = PER_LINE_DISPLAY_FLAG[kind];

    const totals = computeNativeInvoiceTotals({
        lines: state.lines,
        tax: state.tax,
        taxMode: state.taxMode,
        discount: state.discount,
        discountMode: state.discountMode,
        deposit: state.deposit,
        depositMode: state.depositMode,
        isTaxPerLineItem: kind === "tax" ? true : state.isTaxPerLineItem,
        isDiscountPerLineItem:
            kind === "discount" ? true : state.isDiscountPerLineItem,
        isDepositPerLineItem:
            kind === "deposit" ? true : state.isDepositPerLineItem,
    });
    const computedAmount = computedAdjustmentAmountForKind({
        kind,
        totals,
    });

    return {
        ...state,
        [flag]: false,
        // Display-per-line-item requires calculation to also be per-line —
        // clear it alongside the calculation flag so a later re-enable of
        // calculation doesn't silently resurrect a stale display setting the
        // user never re-confirmed.
        [displayFlag]: false,
        [kind]: computedAmount,
        [PER_LINE_MODE[kind]]: "amount",
        lines: state.lines.map((line) => ({ ...line, [kind]: 0 })),
    };
}

export function storedLineAdjustmentFromLabeled({
    labeledValue,
    mode,
    lineAmount,
}: {
    labeledValue: number;
    mode: NativeInvoiceAdjustmentMode;
    lineAmount: number;
}): number {
    return computeNativeInvoiceLineAdjustmentAmount({
        labeledValue,
        mode,
        baseAmount: lineAmount,
    });
}

export function convertNativeInvoicePerLineAdjustmentMode({
    state,
    kind,
    mode,
}: {
    state: NativeInvoiceFormState;
    kind: NativeInvoicePerLineKind;
    mode: NativeInvoiceAdjustmentMode;
}): NativeInvoiceFormState {
    const modeKey = PER_LINE_MODE[kind];
    const fromMode = state[modeKey];
    if (fromMode === mode) {
        return state;
    }
    if (!state[PER_LINE_CALC_FLAG[kind]]) {
        return {
            ...state,
            [modeKey]: mode,
        };
    }

    return {
        ...state,
        [modeKey]: mode,
        lines: state.lines.map((line) => {
            const lineAmount = computeNativeInvoiceLineAmount(line);
            const storedAmount = storedLineAdjustmentFromLabeled({
                labeledValue: line[kind],
                mode: fromMode,
                lineAmount,
            });
            return {
                ...line,
                [kind]: labeledLineAdjustmentFromStored({
                    storedAmount,
                    mode,
                    lineAmount,
                }),
            };
        }),
    };
}
